import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AssetsService } from '../services/assets.service';
import type { MaintenanceAsset, AssetFilters, AssetHealthLog, AssetImage } from '../types/assets.types';
import { useState } from 'react';

export function useAssets(companyId: string, initialFilters: AssetFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<AssetFilters>({
    page: 1,
    pageSize: 10,
    status: 'all',
    health: 'all',
    type: 'all',
    ...initialFilters
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['maintenance-assets', companyId, filters],
    queryFn: () => AssetsService.getAssets(companyId, filters),
    enabled: !!companyId,
  });

  const { data: stats } = useQuery({
    queryKey: ['maintenance-assets', 'stats', companyId],
    queryFn: () => AssetsService.getAssetStats(companyId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<MaintenanceAsset>) => AssetsService.createAsset(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-assets'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<MaintenanceAsset> }) => 
      AssetsService.updateAsset(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-assets'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-assets', 'detail', variables.id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => AssetsService.deleteAsset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-assets'] });
    },
  });

  return {
    assets: data?.data || [],
    total: data?.total || 0,
    stats: stats || { total: 0, goodHealth: 0, warningHealth: 0, badHealth: 0, byType: {} as any },
    isLoading,
    error,
    filters,
    setFilters,
    createAsset: createMutation.mutateAsync,
    updateAsset: updateMutation.mutateAsync,
    deleteAsset: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

export function useAssetDetail(id?: string) {
  return useQuery({
    queryKey: ['maintenance-assets', 'detail', id],
    queryFn: () => AssetsService.getAssetDetail(id!),
    enabled: !!id,
  });
}

export function useAssetHealthLogs(assetId?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['maintenance-assets', 'health-logs', assetId],
    queryFn: () => AssetsService.getAssetHealthLogs(assetId!),
    enabled: !!assetId,
  });

  const createMutation = useMutation({
    mutationFn: (log: Partial<AssetHealthLog>) => AssetsService.createAssetHealthLog(log),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-assets', 'health-logs', assetId] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-assets', 'detail', assetId] });
    },
  });

  return {
    ...query,
    createHealthLog: createMutation.mutateAsync,
    isCreatingLog: createMutation.isPending,
  };
}

export function useAssetGroups(projectId?: string) {
  return useQuery({
    queryKey: ['maintenance-assets', 'groups', projectId],
    queryFn: () => AssetsService.getAssetGroups(projectId!),
    enabled: !!projectId,
  });
}

export function useAssetTypes() {
  return useQuery({
    queryKey: ['maintenance-assets', 'types'],
    queryFn: () => AssetsService.getAssetTypes(),
  });
}

export function useAssetImages(assetId?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['maintenance-assets', 'images', assetId],
    queryFn: () => AssetsService.getAssetImages(assetId!),
    enabled: !!assetId,
  });

  const uploadMutation = useMutation({
    mutationFn: ({ file, type }: { file: File, type?: AssetImage['type'] }) => 
      AssetsService.uploadAssetImage(assetId!, file, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-assets', 'images', assetId] });
    },
  });

  return {
    ...query,
    uploadImage: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
  };
}
